import React from "react";
import { Field, useForm } from "react-final-form";
import { RatioBox } from "@syos-music/common";
import {
  SelectField,
  SliderField,
  TextField,
  Fieldset,
  RadioGroupField,
} from "@syos-music/form";
import {
  InstrumentTypeSelectField,
  MaterialColorSelectField,
  MouthpieceDesignFont,
  MouthpieceDesignFontSelectField,
  MouthpieceDesignSelectField,
} from "@syos-music/instruments";

import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Backdrop,
} from "@mui/material";
import { Delete as DeleteIcon, Upload as UploadIcon } from "mdi-material-ui";

import FileUpload from "components/common/FileUpload";

export default function OptionsField({ isOrderPaid, cartItem, name }) {
  const { instrument } = cartItem.product.defaultOptions;

  const form = useForm();
  const designType = form.getFieldState(`${name}.designType`)?.value;
  const designText = form.getFieldState(`${name}.designText`)?.value;
  const designTextFont = form.getFieldState(`${name}.designTextFont`)?.value;
  const designCustom = form.getFieldState(`${name}.designCustom`)?.value;

  return (
    <Grid container>
      <Grid item xs={12}>
        <InstrumentTypeSelectField
          instrument={instrument}
          name={`${name}.instrumentType`}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <SelectField name={`${name}.level`} label="Niveau">
          <MenuItem value="beginner">Débutant</MenuItem>
          <MenuItem value="intermediate">Intermédiaire</MenuItem>
          <MenuItem value="professional">Professionnel</MenuItem>
        </SelectField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          name={`${name}.musicStyles`}
          label="Styles musicaux"
          parse={(value) => value.split(",")}
          format={(value) => (value || []).join(",")}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <SliderField
          name={`${name}.brightness`}
          label="Brillance"
          min={0}
          max={10}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <SliderField name={`${name}.power`} label="Puisance" min={0} max={10} />
      </Grid>

      <Grid item xs={12}>
        <TextField name={`${name}.comment`} multiline label="Commentaire" />
      </Grid>

      {instrument === "clarinet" && (
        <>
          <Grid item xs={12} sm={6}>
            <TextField name={`${name}.brand`} label="Marque du bec actuel" />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField name={`${name}.model`} label="Modèle du bec actuel" />
          </Grid>

          <Grid item xs={12}>
            <TextField
              name={`${name}.qualitiesAndImprovements`}
              label="Qualités et améliorations"
              multiline
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField name={`${name}.reedsBrand`} label="Marque d’anches" />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField name={`${name}.reedsStrength`} label="Force d’anches" />
          </Grid>
        </>
      )}

      {instrument === "saxophone" && (
        <>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              name={`${name}.brand`}
              fullWidth
              label="Marque du bec actuel"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              name={`${name}.currentTipOpening`}
              fullWidth
              label="Ouverture du bec actuel"
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <SelectField
              name={`${name}.tipOpeningWish`}
              label="Ouverture souhaitée"
              fullWidth
            >
              <MenuItem value="less">Moins ouvert</MenuItem>
              <MenuItem value="same">La même qu’actuellement</MenuItem>
              <MenuItem value="more">Plus ouvert</MenuItem>
            </SelectField>
          </Grid>
        </>
      )}

      <Grid item xs={12} sm={6}>
        <MaterialColorSelectField
          name={`${name}.mouthpieceColor`}
          label="Couleur bec"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <MaterialColorSelectField
          name={`${name}.ligatureColor`}
          label="Couleur ligature"
        />
      </Grid>

      <Grid item xs={12}>
        <Fieldset legend="Design">
          <Grid container alignItems="flex-start">
            <Grid item xs={12} sm={6}>
              <RadioGroupField
                name={`${name}.designType`}
                disabled={isOrderPaid}
                row
              >
                <RadioGroupField.Option value="" label="Aucun" />
                <RadioGroupField.Option value="catalog" label="Syos" />
                <RadioGroupField.Option value="text" label="Text" />
                <RadioGroupField.Option value="custom" label="Custom" />
              </RadioGroupField>
            </Grid>

            {designType === "catalog" && (
              <Grid item xs={12} sm={6}>
                <MouthpieceDesignSelectField
                  name={`${name}.designCatalog`}
                  required
                />
              </Grid>
            )}

            {designType === "text" && (
              <>
                <Grid item xs={12} sm={6}>
                  <MouthpieceDesignFontSelectField
                    name={`${name}.designTextFont`}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MouthpieceDesignFont
                    designFont={designTextFont}
                    sx={{
                      padding: 1,
                      marginTop: 2,
                      backgroundColor: (t) => t.palette.background.odd,
                    }}
                  >
                    {designText ?? " "}
                  </MouthpieceDesignFont>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    name={`${name}.designText`}
                    label="Design text"
                    fullWidth
                    required
                  />
                </Grid>
              </>
            )}

            {designType === "custom" && (
              <Grid item xs={12} sm={6}>
                <Field name={`${name}.designCustom`}>
                  {({ input }) => (
                    <FileUpload
                      uploadPath="custom-designs"
                      isPublic
                      onUpload={(file) => input.onChange(file.url)}
                    >
                      {({ canDrop, inputProps }) => (
                        <div>
                          <Box
                            sx={{
                              position: "relative",
                              marginTop: 1,
                              minHeight: 200,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Backdrop
                              open={canDrop}
                              sx={{
                                position: "absolute",
                                zIndex: (t) => t.zIndex.drawer + 1,
                                color: "#fff",
                              }}
                            >
                              <UploadIcon
                                color="inherit"
                                style={{ fontSize: 120 }}
                              />
                            </Backdrop>

                            {designCustom ? (
                              <Grid
                                container
                                spacing={1}
                                justifyContent="center"
                              >
                                <Grid item xs={12}>
                                  <RatioBox ratio={16 / 9}>
                                    <Box
                                      component="img"
                                      src={designCustom}
                                      alt=""
                                      sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                      }}
                                    />
                                  </RatioBox>
                                </Grid>

                                <Grid item>
                                  <IconButton
                                    onClick={() => input.onChange(null)}
                                    size="large"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            ) : (
                              <Grid
                                container
                                spacing={2}
                                justifyContent="center"
                              >
                                <Grid item>
                                  <Box
                                    sx={{
                                      width: 0.1,
                                      height: 0.1,
                                      opacity: 0,
                                      overflow: "hidden",
                                      position: "absolute",
                                      zIndex: -1,
                                    }}
                                    component="input"
                                    {...inputProps}
                                    id="file"
                                  />
                                  <Button
                                    component="label"
                                    htmlFor="file"
                                    startIcon={<UploadIcon />}
                                  >
                                    Upload
                                  </Button>
                                </Grid>
                              </Grid>
                            )}
                          </Box>
                        </div>
                      )}
                    </FileUpload>
                  )}
                </Field>
              </Grid>
            )}
          </Grid>
        </Fieldset>
      </Grid>
    </Grid>
  );
}
